/**
 * EarningsCard.jsx — earnings/EarningsCard.jsx
 *
 * Collapsible card for one earnings stock. Shows:
 *   Collapsed: rank, ticker, report date/time, price, implied move, IV rank, vol bar
 *   Expanded:  directional probability, predicted range, 8-metric grid,
 *              52W range bar, bull/bear thesis
 *
 * The top card (#1 by volatility score) gets an accent-coloured border and
 * a "TOP VOLATILITY PICK" banner.
 */

import { VolBar, ProbBar, SkewBadge, fmt, fmtPct } from './EarningsShared';

export default function EarningsCard({ stock, rank, t, expanded, onToggle }) {
  const rangeSpan = Math.abs(stock.predictedRangeHigh ?? 5) + Math.abs(stock.predictedRangeLow ?? 5);

  return (
    <div style={{
      background: t.cardBg,
      border: `1px solid ${rank === 0 ? t.accent : t.border}`,
      borderRadius: 8, overflow: 'hidden', marginBottom: 10,
    }}>
      {/* Top pick banner */}
      {rank === 0 && (
        <div style={{ background: t.accent, padding: '3px 12px' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: t.bg, letterSpacing: '.1em' }}>
            ⚡ TOP VOLATILITY PICK
          </span>
        </div>
      )}

      {/* ── Collapsed header ── */}
      <div
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: '48px 1fr auto auto auto auto',
          gap: '0 10px', alignItems: 'center',
          padding: '10px 14px', cursor: 'pointer',
          background: expanded ? t.bgHover : 'transparent',
        }}
      >
        {/* Rank + vol score */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: rank < 3 ? t.accent : t.muted }}>#{rank + 1}</div>
          <div style={{ fontSize: 9, color: t.faint }}>VOL {stock.volatilityScore}</div>
        </div>

        {/* Ticker + company */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: t.text }}>{stock.ticker}</span>
            {/* BMO = before market open (green), AMC = after close (red) */}
            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: stock.reportTime === 'BMO' ? t.upSoft : t.downSoft, color: stock.reportTime === 'BMO' ? t.up : t.down }}>
              {stock.reportTime}
            </span>
            {stock.reportDate && (
              <span style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 600, color: t.accent, background: 'rgba(88,166,255,0.1)', padding: '1px 6px', borderRadius: 3 }}>
                📅 {stock.reportDate}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: t.muted, marginTop: 1 }}>
            {stock.company} · {stock.sector} · {stock.marketCap}
          </div>
        </div>

        {/* Price + avg volume */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: t.text }}>${fmt(stock.currentPrice)}</div>
          <div style={{ fontSize: 9, color: t.faint }}>{stock.avgVolume} avg vol</div>
        </div>

        {/* Implied move — the options straddle-implied ±% move */}
        <div style={{ textAlign: 'center', minWidth: 54 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: t.warn }}>±{fmt(stock.impliedMove)}%</div>
          <div style={{ fontSize: 9, color: t.faint }}>impl. move</div>
        </div>

        {/* IV Rank — colour coded: red ≥70, yellow ≥40, green <40 */}
        <div style={{ textAlign: 'center', minWidth: 42 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600, color: stock.ivRank >= 70 ? t.down : stock.ivRank >= 40 ? t.warn : t.up }}>
            {stock.ivRank}
          </div>
          <div style={{ fontSize: 9, color: t.faint }}>IV Rank</div>
        </div>

        <div style={{ color: t.faint, fontSize: 14, userSelect: 'none' }}>{expanded ? '▲' : '▼'}</div>
      </div>

      {/* Vol bar always visible below header */}
      <div style={{ padding: '0 14px 8px' }}>
        <VolBar score={stock.volatilityScore} t={t} />
      </div>

      {/* ── Expanded detail ── */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${t.border}` }}>

          {/* Directional probability + predicted range */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '12px 14px', borderBottom: `1px solid ${t.border}`, gap: 16 }}>
            <div>
              <div style={{ fontSize: 9, color: t.muted, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Directional Probability</div>
              <ProbBar probUp={stock.probUp ?? 50} t={t} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: t.muted, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Predicted Range</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: t.down, fontWeight: 600 }}>{fmtPct(stock.predictedRangeLow)}</span>
                <div style={{ flex: 1, height: 8, borderRadius: 4, background: t.border, overflow: 'hidden', display: 'flex' }}>
                  <div style={{ width: `${Math.abs(stock.predictedRangeLow ?? 5) / rangeSpan * 100}%`, background: t.downSoft, borderRight: `2px solid ${t.down}` }} />
                  <div style={{ width: `${(stock.predictedRangeHigh ?? 5) / rangeSpan * 100}%`, background: t.upSoft, borderLeft: `2px solid ${t.up}` }} />
                </div>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: t.up, fontWeight: 600 }}>{fmtPct(stock.predictedRangeHigh)}</span>
              </div>
            </div>
          </div>

          {/* 8-metric grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: `1px solid ${t.border}` }}>
            {[
              ['IV30',       `${fmt(stock.iv30)}%`,               t.text],
              ['Hist. Move', `±${fmt(stock.historicalAvgMove)}%`, t.text],
              ['EPS Est.',   `$${fmt(stock.estimatedEPS)}`,       t.text],
              ['EPS Growth', `${fmt(stock.epsGrowthPct)}%`,       (stock.epsGrowthPct ?? 0) > 0 ? t.up : t.down],
              ['Analyst',    stock.analystRating || '—',          (stock.analystRating || '').includes('Buy') ? t.up : t.down],
              ['Bull/Bear',  `${stock.bullBearRatio ?? '—'}% bull`, (stock.bullBearRatio ?? 50) >= 60 ? t.up : (stock.bullBearRatio ?? 50) <= 40 ? t.down : t.warn],
              ['Rev. Est.',  stock.revenueEst || '—',             t.text],
              ['Skew',       <SkewBadge key="sk" skew={stock.optionSkew} t={t} />, null],
            ].map(([lbl, val, color]) => (
              <div key={lbl} style={{ padding: '8px 12px', borderRight: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}` }}>
                <div style={{ fontSize: 9, color: t.muted, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 3 }}>{lbl}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: color || t.text }}>{val}</div>
              </div>
            ))}
          </div>

          {/* 52W range bar */}
          {stock.weekHigh52 && stock.weekLow52 && (
            <div style={{ padding: '8px 14px', borderBottom: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 9, color: t.muted, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 5 }}>52-week range</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: t.muted }}>${fmt(stock.weekLow52)}</span>
                <div style={{ flex: 1, height: 5, background: t.border, borderRadius: 3, position: 'relative' }}>
                  <div style={{
                    position: 'absolute', left: 0, height: '100%', borderRadius: 3, background: t.accent,
                    width: `${((stock.currentPrice - stock.weekLow52) / (stock.weekHigh52 - stock.weekLow52) * 100)}%`,
                  }} />
                </div>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: t.muted }}>${fmt(stock.weekHigh52)}</span>
              </div>
            </div>
          )}

          {/* Bull / bear thesis */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <div style={{ padding: '10px 14px', borderRight: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 9, color: t.up, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>↑ Bull case</div>
              <div style={{ fontSize: 12, color: t.muted, lineHeight: 1.5 }}>{stock.keyBull}</div>
            </div>
            <div style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 9, color: t.down, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>↓ Bear risk</div>
              <div style={{ fontSize: 12, color: t.muted, lineHeight: 1.5 }}>{stock.keyRisk}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
